/** Balance summary card component showing total/used/available credit. */
import React from "react";
import { Card, CardContent, Grid, Typography, LinearProgress, Box } from "@mui/material";
import { CheckCircle, Warning, Error } from "@mui/icons-material";

interface BalanceSummary {
  total_credit: number;
  used_credit: number;
  available_credit: number;
  currency: string;
}

interface BalanceCardProps {
  summary: BalanceSummary;
}

/** Format number as currency. */
const formatCurrency = (value: number, currency: string = "USD"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/** Determine color based on usage percentage. */
const getUsageColor = (percentage: number): "success" | "warning" | "error" => {
  if (percentage < 50) return "success";
  if (percentage < 80) return "warning";
  return "error";
};

/** Get status icon based on usage percentage. */
const getStatusIcon = (percentage: number) => {
  if (percentage < 50) return <CheckCircle color="success" />;
  if (percentage < 80) return <Warning color="warning" />;
  return <Error color="error" />;
};

/**
 * BalanceCard component.
 *
 * Displays three metric cards showing:
 * - Total Credit (green)
 * - Used Credit (red)
 * - Available Credit (blue)
 *
 * Includes a progress bar showing usage percentage with color coding.
 */
const BalanceCard: React.FC<BalanceCardProps> = ({ summary }) => {
  const usagePercentage =
    summary.total_credit > 0
      ? (summary.used_credit / summary.total_credit) * 100
      : 0;

  const usageColor = getUsageColor(usagePercentage);

  return (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" component="h2">
            Balance Summary
          </Typography>
          {getStatusIcon(usagePercentage)}
        </Box>

        <Grid container spacing={3}>
          {/* Total Credit */}
          <Grid item xs={12} sm={4}>
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Credit
              </Typography>
              <Typography
                variant="h4"
                component="div"
                color="success.main"
                fontWeight="bold"
              >
                {formatCurrency(summary.total_credit, summary.currency)}
              </Typography>
            </Box>
          </Grid>

          {/* Used Credit */}
          <Grid item xs={12} sm={4}>
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Used Credit
              </Typography>
              <Typography
                variant="h4"
                component="div"
                color="error.main"
                fontWeight="bold"
              >
                {formatCurrency(summary.used_credit, summary.currency)}
              </Typography>
            </Box>
          </Grid>

          {/* Available Credit */}
          <Grid item xs={12} sm={4}>
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available Credit
              </Typography>
              <Typography
                variant="h4"
                component="div"
                color="primary.main"
                fontWeight="bold"
              >
                {formatCurrency(summary.available_credit, summary.currency)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Progress Bar */}
        <Box mt={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2" color="text.secondary">
              Credit Usage
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {usagePercentage.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={usagePercentage}
            color={usageColor}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default BalanceCard;
